// src/router/index.js
import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '../views/HomeView.vue'
import PackageView from '../views/PackageView.vue'
import AccountView from '../views/AccountView.vue'
import EsimView from '../views/EsimView.vue'
import OrdersView from '../views/OrdersView.vue'
import InvitationView from '@/views/InvitationView.vue'
// import StripeView from '@/views/StripeView.vue'
import ContactView from '@/views/ContactView.vue'
import InvitationListView from "@/views/InvitationListView.vue"
import AddressView from '@/views/AddressView.vue'
import AddAddressView from '@/views/AddAddressView.vue'
import PaymentStatusView from '@/views/PaymentStatusView.vue'
import ReturnPolicyView from '@/views/ReturnPolicyView.vue'

import LoginView from '@/views/LoginView.vue'
import RegisterView from '@/views/RegisterView.vue'
import ForgotPasswordView from '@/views/ForgotPasswordView.vue'
import { getStorage } from '@/utils/storage'
import { STORAGE_KEYS } from '@/constants/storageKeys'
import logger from '@/utils/logger'
import { trackPageView } from '@/tracking'
import { resetPageScroll } from '@/utils/scrollReset'
import { syncTelegramBackButton } from '@/utils/telegramBackButton'
import { nextTick } from 'vue'
import le from '@/views/pm/le.vue' 
import lg from '@/views/pm/lg.vue' 

const routes = [
  {
    path: '/',
    name: 'HomeView',
    component: HomeView,
    meta: { title: 'LeSIM - Global eSIM & SIM Card Data Plans', hideTopActions: true, keepAlive: true }
  },
  {
    path: '/package/:countryCode',
    name: 'PackageView',
    component: PackageView,
    meta: { keepAlive: true, title: 'Choose Your Plan - LeSIM', hideTopActions: true, tgBackTo: '/' }
  },
  {
      path: '/account',
      name: 'account',
      component: AccountView,
      meta: { title: 'My Account - LeSIM', hideTopActions: true, tgBackTo: '/' }
    },
    {
      path: '/coupons',
      name: 'coupons',
      component: () => import('../views/CouponListView.vue'),
      meta: { title: 'My Coupons - LeSIM', hideTopActions: true, hasFilterTabs: true }
    },
  {
    path: '/esim',
    name: 'EsimView',
    component: EsimView,
    meta: { title: 'My eSIMs - LeSIM' }
  },
  {
    path: '/orders',
    name: 'OrdersView',
    component: OrdersView,
    meta: { title: 'My Orders - LeSIM', hideTopActions: true, tgBackTo: '/account', hasFilterTabs: true }
  },
  {
    path: '/invitation',
    name: 'InvitationView',
    component: InvitationView,
    meta: { title: 'Invite Friends - LeSIM', hideTopActions: true }
  },
  // {
  //   path: '/stripe/:orderNo',
  //   name: 'StripeView',
  //   component: StripeView
  // },
  {
    path: '/contact',
    name: 'ContactView',
    component: ContactView,
    meta: { title: 'Contact Us - LeSIM', hideTopActions: true }
  },
  {
    path: '/invitationlist',
    name: 'InvitationListView',
    component: InvitationListView,
    meta: { title: 'Invitation History - LeSIM', hideTopActions: true }
  },
  {
    path: '/address',
    name: 'AddressView',
    component: AddressView,
    meta: { title: 'My Addresses - LeSIM', hideTopActions: true }
  },
  {
    path: '/address/add',
    name: 'AddAddressView',
    component: AddAddressView,
    meta: { title: 'Add Address - LeSIM', hideTopActions: true }
  },
  {
    path: '/payment-status',
    name: 'PaymentStatusView',
    component: PaymentStatusView,
    meta: { title: 'Payment Status - LeSIM', hideTopActions: true, tgBackTo: '/' }
  },
  {
    path: '/return-policy',
    name: 'ReturnPolicyView',
    component: ReturnPolicyView,
    meta: { title: 'Return Policy - LeSIM', hideTopActions: true }
  },
  {
    path: '/legal/:policyType',
    name: 'LegalPolicyView',
    component: () => import('../views/LegalPolicyView.vue'),
    meta: { title: 'Legal - LeSIM', hideTopActions: true }
  },

  {
    path: '/login',
    name: 'LoginView',
    component: LoginView,
    meta: { requiresAuth: false, isPublic: true, title: 'Sign In - LeSIM', tgBackTo: '/' }
  },
  {
    path: '/register',
    name: 'RegisterView',
    component: RegisterView,
    meta: { requiresAuth: false, isPublic: true, title: 'Sign Up - LeSIM', tgBackTo: '/' }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPasswordView',
    component: ForgotPasswordView,
    meta: { requiresAuth: false, isPublic: true, title: 'Forgot Password - LeSIM', tgBackTo: '/login' }
  },
  {
    path: '/pm/le',
    name: 'le',
    component: le,
    meta: { requiresAuth: false, isPublic: true, title: 'LeSIM - Global eSIM Service' }
  },
  {
    path: '/pm/lg',
    name: 'lg',
    component: lg,
    meta: { requiresAuth: false, isPublic: true, title: 'LeSIM - Global eSIM Service' }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.path === from.path && to.fullPath === from.fullPath) {
      return false
    }
    return { top: 0, left: 0 }
  }
})


// 检查是否在 Telegram 环境中
const isTelegramEnv = () => {
  if (typeof window === 'undefined') {
    return false
  }
  try {
    // 检查 Telegram WebApp 是否存在
    const webApp = window.Telegram?.WebApp
    if (!webApp) {
      logger.debug('[Router] Not in Telegram: window.Telegram.WebApp not found')
      return false
    }
    // 检查是否有有效的 initData（非空字符串）
    const initData = webApp.initData
    if (!initData || typeof initData !== 'string' || initData.trim() === '') {
      logger.debug('[Router] Not in Telegram: initData is empty or invalid', { 
        hasInitData: !!initData, 
        type: typeof initData,
        length: initData?.length 
      })
      return false
    }
    // 额外检查：确保 initData 看起来像有效的 Telegram initData
    // Telegram initData 通常包含查询参数格式的数据
    if (initData.trim().length < 10) {
      logger.debug('[Router] Not in Telegram: initData too short')
      return false
    }
    logger.debug('[Router] In Telegram environment')
    return true
  } catch (error) {
    // 如果访问 Telegram 对象时出错，说明不在 Telegram 环境
    logger.debug('[Router] Not in Telegram: error accessing Telegram object', error)
    return false
  }
}

// 检查是否已登录
const isAuthenticated = () => {
  const token = getStorage(STORAGE_KEYS.TOKEN)
  // token 可能是字符串或解析后的值，确保是有效的非空字符串
  const tokenStr = typeof token === 'string' ? token : String(token || '')
  const isValid = tokenStr.trim() !== '' && tokenStr !== 'null' && tokenStr !== 'undefined'
  logger.debug('[Router] Auth check:', { 
    hasToken: !!token, 
    tokenType: typeof token,
    tokenLength: tokenStr.length,
    isValid 
  })
  return isValid
}

router.beforeEach((to, from, next) => {
  const inTelegram = isTelegramEnv()
  const authenticated = isAuthenticated()
  
  logger.debug('[Router Guard]', {
    to: to.name,
    from: from.name,
    inTelegram,
    authenticated,
    isPublic: to.meta?.isPublic
  })

  // 首页永远放行，不做登录拦截
  if (to.path === '/') {
    logger.debug('[Router Guard] Home route, allowing access')
    next()
    return
  }
  
  // 如果是公开页面（登录/注册），直接允许访问
  if (to.meta?.isPublic) {
    logger.debug('[Router Guard] Public route, allowing access')
    next()
    return
  }
  
  // 如果在 Telegram 环境中，直接允许访问（Telegram 会自动登录）
  if (inTelegram) {
    logger.debug('[Router Guard] In Telegram, allowing access')
    next()
    return
  }
  
  // 如果不在 Telegram 环境中，必须检查登录状态
  if (!authenticated) {
    logger.debug('[Router Guard] Not authenticated, redirecting to login')
    // 未登录，重定向到登录页，保存原始路径以便登录后跳转
    // 避免重复重定向到登录页
    if (to.name !== 'LoginView' && to.name !== 'RegisterView') {
      const campaign = String(to.query?.campaign || '').trim()
      next({
        name: 'LoginView',
        query: {
          redirect: to.fullPath !== '/' ? to.fullPath : undefined,
          campaign: campaign || undefined,
          lang: to.query?.lang
        }
      })
      return
    }
  }
  
  // 已登录，允许访问
  logger.debug('[Router Guard] Authenticated, allowing access')
  next()
});

const BODY_CLASS_FILTER_PAGE = 'tg-filter-page'
const BODY_CLASS_SURFACE_WHITE = 'tg-surface-white'
const WHITE_SURFACE_ROUTES = new Set([
  'OrdersView',
  'AddressView',
  'AddAddressView',
])

router.afterEach((to, from) => {
  const isRealNavigation = to.fullPath !== from.fullPath
  if (isRealNavigation && to.name !== 'HomeView') {
    resetPageScroll()
    nextTick(() => {
      resetPageScroll()
      requestAnimationFrame(() => resetPageScroll())
    })
  }

  const hasFilterTabs = !!to.meta?.hasFilterTabs
  document.body.classList.toggle(BODY_CLASS_FILTER_PAGE, hasFilterTabs)
  document.documentElement.classList.toggle(BODY_CLASS_FILTER_PAGE, hasFilterTabs)

  const useWhiteSurface = WHITE_SURFACE_ROUTES.has(to.name)
  document.body.classList.toggle(BODY_CLASS_SURFACE_WHITE, useWhiteSurface)
  document.documentElement.classList.toggle(BODY_CLASS_SURFACE_WHITE, useWhiteSurface)

  const pageTitle = to.meta?.title || 'LeSIM'
  document.title = pageTitle
  trackPageView(to.path, pageTitle)
  syncTelegramBackButton(to, router)
})

export default router
